/* Processing-team parcel notes + temporary highlights (row / cell / note) for pending parcels. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StickyNote, Trash2, X } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const HL = {
  yellow: { bg: "#fef08a", ring: "#eab308", label: "Yellow" },
  green: { bg: "#bbf7d0", ring: "#22c55e", label: "Green" },
  red: { bg: "#fecaca", ring: "#ef4444", label: "Red" },
  blue: { bg: "#bfdbfe", ring: "#3b82f6", label: "Blue" },
};
const PER_ROW = ["medicine", "quantity", "qty1", "price"];
const CELL_OPTS = [["tracking", "Tracking"], ["customer_name", "Customer"], ["code", "Code"], ["country", "Country"],
  ["mode", "Service"], ["second_name", "Second name"], ["reason", "Reason"], ["invoice_no", "Invoice #"],
  ["notes", "Notes"], ["date", "Date"]];

export const hlCell = (r, first, f) => {
  const m = r.pnote?.cell_hl || {};
  const c = PER_ROW.includes(f) ? m[`${f}@${r.id}`] : first ? m[f] : null;
  return c ? `or-hl-cell or-hl-${c}` : "";
};
export const rowHlStyle = (r) => (r.pnote?.row_hl ? { backgroundColor: HL[r.pnote.row_hl].bg } : null);

const Dots = ({ value, onPick, testid }) => (
  <span className="inline-flex items-center gap-1">
    {Object.entries(HL).map(([k, v]) => (
      <button key={k} type="button" title={v.label} onClick={() => onPick(value === k ? null : k)} data-testid={`${testid}-${k}`}
        className={`w-4 h-4 rounded-full border transition-transform hover:scale-110 ${value === k ? "ring-2 ring-offset-1 ring-slate-700" : ""}`}
        style={{ backgroundColor: v.bg, borderColor: v.ring }} />
    ))}
    {value && <button type="button" onClick={() => onPick(null)} title="Clear" data-testid={`${testid}-clear`}
      className="text-slate-400 hover:text-slate-700"><X className="w-3.5 h-3.5" /></button>}
  </span>
);

const ago = (iso) => new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const Thread = ({ notes, readOnly, me, isAdmin, call, rowId }) => (
  <div className="max-h-52 overflow-y-auto space-y-1.5" data-testid={`pn-thread-${rowId}`}>
    {notes.length ? notes.map((n, i) => (
      <div key={n.id} className="rounded border border-slate-200 p-1.5" style={n.hl ? { backgroundColor: HL[n.hl].bg } : undefined}
        data-testid={`pn-note-${rowId}-${i}`}>
        <div className="flex items-center gap-1 text-[10px] text-slate-500">
          <b className="text-slate-700">{n.by_name}</b> · {ago(n.at)}
          {!readOnly && <span className="ml-auto flex items-center gap-1">
            <Dots value={n.hl} onPick={(c) => call("put", "highlight", { target: `note:${n.id}`, color: c })} testid={`pn-note-hl-${i}`} />
            {(isAdmin || n.by_id === me) && <button type="button" onClick={() => call("delete", n.id)} title="Delete note"
              className="text-slate-400 hover:text-rose-600" data-testid={`pn-note-del-${i}`}><Trash2 className="w-3 h-3" /></button>}
          </span>}
        </div>
        <div className="text-xs text-slate-800 whitespace-pre-wrap break-words">{n.text}</div>
      </div>
    )) : <div className="text-[11px] text-slate-400">No notes yet.</div>}
  </div>
);

const HighlightBox = ({ d, rows, call }) => {
  const [cell, setCell] = useState("tracking");
  const opts = [...CELL_OPTS, ...rows.flatMap((r) => PER_ROW.slice(0, 3).map((f) => [`${f}@${r.id}`, `${f === "medicine" ? "Product" : f === "quantity" ? "Qty" : "Qty1"}: ${r.medicine || "—"}`]))];
  return (
    <div className="space-y-1.5 border-t pt-2" data-testid="pn-hl-box">
      <div className="flex items-center gap-2 text-[11px]"><span className="w-16 font-semibold text-slate-600">Row</span>
        <Dots value={d.row_hl} onPick={(c) => call("put", "highlight", { target: "row", color: c })} testid="pn-row-hl" /></div>
      <div className="flex items-center gap-2 text-[11px]"><span className="w-16 font-semibold text-slate-600">Cell</span>
        <select value={cell} onChange={(e) => setCell(e.target.value)} className="h-6 max-w-[150px] rounded border px-1 text-[11px]" data-testid="pn-cell-select">
          {opts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <Dots value={(d.cell_hl || {})[cell]} onPick={(c) => call("put", "highlight", { target: cell, color: c })} testid="pn-cell-hl" /></div>
      {(d.row_hl || Object.keys(d.cell_hl || {}).length > 0) && (
        <button type="button" onClick={() => call("put", "highlight", { target: "clear", color: null })} data-testid="pn-hl-clear-all"
          className="text-[11px] text-slate-500 underline hover:text-slate-800">Clear all highlights</button>)}
      <div className="text-[10px] text-slate-400">Highlights stay only while the parcel is pending.</div>
    </div>
  );
};

export const ParcelNoteBadge = ({ row, rows = [row], readOnly, isAdmin, me, onChanged, source }) => {
  const hdr = () => ({ ...getAuthHeader(), ...(source ? { "X-Panel-Source": source } : {}) });
  const [d, setD] = useState(null);
  const [text, setText] = useState("");
  const [hl, setHl] = useState(null);
  const [busy, setBusy] = useState(false);
  const pn = row.pnote;
  if (readOnly && !pn) return null;
  const base = `${API}/order-register/parcel-notes/${row.id}`;
  const load = async () => {
    try { setD((await axios.get(base, { headers: getAuthHeader() })).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not load notes"); }
  };
  const call = async (method, path, body) => {
    setBusy(true);
    try {
      const url = path ? `${base}/${path}` : base;
      const r = method === "delete" ? await axios.delete(url, { headers: hdr() })
        : await axios[method](url, body, { headers: hdr() });
      setD(r.data); onChanged?.();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); return false; } finally { setBusy(false); }
  };
  const add = async () => { if (text.trim() && await call("post", "", { text, hl })) { setText(""); setHl(null); } };
  const last = pn?.last;
  return (
    <Popover onOpenChange={(o) => o && load()}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} data-testid={`or-pnote-badge-${row.id}`}
          title={last ? `${last.by_name}: ${last.text}` : "Add a team note / highlight"}
          className={`ml-1 inline-flex items-center gap-0.5 align-middle rounded px-1 py-px text-[9px] font-bold ${pn?.count
            ? "bg-yellow-300 text-yellow-900 border border-yellow-500" : "text-sky-600 opacity-40 hover:opacity-100"}`}>
          <StickyNote className="w-3 h-3" />{pn?.count ? pn.count : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-96 max-w-[calc(100vw-1.5rem)] p-3 space-y-2" align="start" collisionPadding={12} onClick={(e) => e.stopPropagation()} data-testid={`pn-pop-${row.id}`}>
        <div className="text-xs font-bold text-slate-700">Team notes · {row.customer_name}</div>
        {!d ? <div className="text-[11px] text-slate-400">Loading…</div> : (<>
          <Thread notes={d.notes || []} readOnly={readOnly} me={me} isAdmin={isAdmin} call={call} rowId={row.id} />
          {!readOnly && (
            <div className="space-y-1">
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={500} placeholder="Note for the team (e.g. check stock before invoicing)"
                className="w-full rounded border border-slate-300 p-1.5 text-xs" data-testid="pn-note-input" />
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-slate-500">Mark note</span><Dots value={hl} onPick={setHl} testid="pn-new-hl" />
                <button type="button" disabled={busy || !text.trim()} onClick={add} data-testid="pn-note-add"
                  className="ml-auto rounded bg-sky-600 px-3 py-1 font-semibold text-white hover:bg-sky-700 disabled:opacity-50">Add note</button>
              </div>
            </div>)}
          {!readOnly && d.pending && <HighlightBox d={d} rows={rows} call={call} />}
          {readOnly && <div className="text-[10px] text-slate-400">View-only — the processing team adds notes.</div>}
        </>)}
      </PopoverContent>
    </Popover>
  );
};
